import {
    buildReportingConsolidationScope,
    buildReportingEntityScope,
    REPORTING_SCOPE_QUERY_PARAM,
} from "./reportingScope";

export const REPORTING_ROUTE = "/reporting";

export function buildReportingEntityRoute(entityId: number): string {
    const search = new URLSearchParams({
        [REPORTING_SCOPE_QUERY_PARAM]: buildReportingEntityScope(entityId),
    });
    return `${REPORTING_ROUTE}?${search.toString()}`;
}

export function buildReportingConsolidationRoute(groupId: string): string {
    const search = new URLSearchParams({
        [REPORTING_SCOPE_QUERY_PARAM]:
            buildReportingConsolidationScope(groupId),
    });
    return `${REPORTING_ROUTE}?${search.toString()}`;
}

/**
 * Report path segments served under `/accounting/*` for backward compatibility.
 * These routes belong to Reporting even though their URL still begins with
 * `/accounting/` — breadcrumbs still treat them as Reporting.
 */
export const REPORT_PATH_SEGMENTS: readonly string[] = [
    "profit-loss",
    "balance-sheet",
    "general-ledger",
    "trial-balance",
    "expenses-by-vendor",
    "cash-flow-statement",
    "ar-aging-summary",
    "ar-aging-detail",
    "check-detail",
    "schedules",
    "elimination-detail",
    "elimination-exceptions",
];

/**
 * P&L and Balance Sheet have dedicated bookmarks under each entity and
 * consolidation group. The Workflows Reporting item must stay inactive there.
 */
const ENTITY_NAV_REPORT_SEGMENTS: readonly string[] = [
    "profit-loss",
    "balance-sheet",
];

function normalize(pathname: string): string {
    if (pathname.length > 1 && pathname.endsWith("/")) {
        return pathname.slice(0, -1);
    }
    return pathname;
}

function matchesSegment(
    pathname: string,
    segments: readonly string[],
): boolean {
    return segments.some(
        (segment) =>
            pathname === `/accounting/${segment}` ||
            pathname.startsWith(`/accounting/${segment}/`),
    );
}

/** True when the page is a report (catalog, breadcrumbs, and related chrome). */
export function isReportingRoute(pathname: string): boolean {
    const path = normalize(pathname);
    if (path === REPORTING_ROUTE) {
        return true;
    }
    return matchesSegment(path, REPORT_PATH_SEGMENTS);
}

/**
 * True when the top-level Reporting nav item should be highlighted.
 * Entity/group P&L and Balance Sheet bookmarks own that selection instead.
 */
export function isReportingNavRoute(pathname: string): boolean {
    const path = normalize(pathname);
    return (
        !matchesSegment(path, ENTITY_NAV_REPORT_SEGMENTS) &&
        isReportingRoute(pathname)
    );
}
